import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Check, Image as ImageIcon, Loader2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";
import { useAuth } from "../context/AuthContext";
import { CATEGORY_META } from "./Services";

const ServiceSubmit = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [submitted, setSubmitted] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploadingIdx, setUploadingIdx] = useState(-1);
    const [form, setForm] = useState({
        kind: "offer",
        title: "",
        description: "",
        category: "goods",
        business_name: "",
        address: "",
        quantity_available: 1,
        unit_label: "item",
        contact_preference: "either",
        images: [],
    });

    useEffect(() => {
        if (!authLoading && !user) {
            toast.error("Please sign in to post a listing.");
            navigate(`/login?next=${encodeURIComponent("/services/submit")}`);
        }
    }, [authLoading, user, navigate]);

    const upload = async (file, idx) => {
        setUploadingIdx(idx);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = data?.url || data?.file_url;
            if (url) {
                const next = [...form.images];
                next[idx] = url;
                setForm((p) => ({ ...p, images: next.filter(Boolean) }));
                toast.success("Photo added");
            } else {
                toast.error("Upload returned no URL");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingIdx(-1);
        }
    };

    const removeImage = (idx) => {
        const next = form.images.filter((_, i) => i !== idx);
        setForm((p) => ({ ...p, images: next }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form, quantity_available: parseInt(form.quantity_available, 10) || 1 };
            const { data } = await api.post("/services", payload);
            setSubmitted(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit listing");
        } finally {
            setSubmitting(false);
        }
    };

    if (authLoading || !user) {
        return <div className="min-h-screen bg-[#FDFBF7] py-20 text-center text-sm text-[#4A5568]">Checking sign-in…</div>;
    }

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="service-submit-page">
            <SEO title="Post a listing — Community board" description="Offer goods or services to the community, or ask the community for help. Free, moderated, no money handled." path="/services/submit" />
            <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
                <Link to="/services" className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#E53935] mb-6">
                    <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to the board
                </Link>

                {submitted ? (
                    <div className="bg-white border border-green-200 rounded-2xl p-8 text-center" data-testid="service-submit-success">
                        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                            <Check className="w-6 h-6 text-green-700" strokeWidth={2} />
                        </div>
                        <h1 className="font-heading text-3xl text-[#1C252E] tracking-tight">Submitted for review</h1>
                        <p className="text-sm text-[#4A5568] mt-3 max-w-md mx-auto">Thanks — a foundation moderator will review &ldquo;{submitted.title}&rdquo; shortly. You&apos;ll get an email the moment it&apos;s approved. From there, the community can request it directly.</p>
                        <div className="flex gap-3 justify-center mt-6">
                            <Link to="/services" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium">Back to the board</Link>
                            <button onClick={() => { setSubmitted(null); setForm({ ...form, title: "", description: "", quantity_available: 1, images: [] }); }} className="inline-flex items-center gap-1 border border-black/10 hover:bg-[#F4F1EA] rounded-full px-5 py-2 text-sm font-medium">Post another</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <header className="mb-8">
                            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Community board · everything free</p>
                            <h1 className="font-heading text-4xl text-[#1C252E] font-medium tracking-tight leading-[1.05]">Post a listing</h1>
                            <p className="text-base text-[#4A5568] mt-4">Share what you have, or ask the community for help. A moderator will review before it goes public.</p>
                        </header>

                        <form onSubmit={submit} className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="service-submit-form">
                            {/* Kind toggle */}
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2 block">Type</label>
                                <div className="inline-flex bg-[#F4F1EA] rounded-full p-1">
                                    {[
                                        { v: "offer", label: "I'm offering" },
                                        { v: "request", label: "I need help" },
                                    ].map((k) => (
                                        <button
                                            key={k.v}
                                            type="button"
                                            data-testid={`submit-kind-${k.v}`}
                                            onClick={() => setForm({ ...form, kind: k.v })}
                                            className={`px-4 py-1.5 text-sm rounded-full transition ${form.kind === k.v ? "bg-[#E53935] text-white font-medium" : "text-[#4A5568] hover:text-[#1C252E]"}`}
                                        >
                                            {k.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label htmlFor="sub-title" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Title *</label>
                                    <input id="sub-title" data-testid="submit-title-input" required maxLength={140} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="Free firewood delivery within 20mi" />
                                </div>
                                <div>
                                    <label htmlFor="sub-cat" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Category</label>
                                    <select id="sub-cat" data-testid="submit-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40">
                                        {Object.entries(CATEGORY_META).map(([slug, m]) => <option key={slug} value={slug}>{m.label}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="sub-desc" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Description *</label>
                                <textarea id="sub-desc" rows={4} data-testid="submit-description-input" required minLength={10} maxLength={4000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="Be specific — what's available, condition, pickup vs delivery, timing." />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label htmlFor="sub-qty" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Quantity</label>
                                    <input id="sub-qty" data-testid="submit-quantity-input" type="number" min="1" max="10000" required value={form.quantity_available} onChange={(e) => setForm({ ...form, quantity_available: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="sub-unit" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Unit label</label>
                                    <input id="sub-unit" data-testid="submit-unit-input" required value={form.unit_label} onChange={(e) => setForm({ ...form, unit_label: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="cord · dozen · hour · bale · backpack…" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label htmlFor="sub-biz" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Business name (if any)</label>
                                    <input id="sub-biz" data-testid="submit-business-input" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="Smith Hardware (optional)" />
                                </div>
                                <div>
                                    <label htmlFor="sub-addr" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">City / Address *</label>
                                    <input id="sub-addr" data-testid="submit-address-input" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="Town (full address kept private)" />
                                </div>
                            </div>

                            {/* Images */}
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2 block">Photos (up to 3)</label>
                                <div className="grid grid-cols-3 gap-3">
                                    {[0, 1, 2].map((idx) => {
                                        const existing = form.images[idx];
                                        return existing ? (
                                            <div key={idx} className="relative aspect-square">
                                                <img src={existing} alt="" className="w-full h-full object-cover rounded-xl" />
                                                <button type="button" onClick={() => removeImage(idx)} data-testid={`submit-image-remove-${idx}`} aria-label="Remove" className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80">
                                                    <X className="w-4 h-4" strokeWidth={2} />
                                                </button>
                                            </div>
                                        ) : (
                                            <label key={idx} className="aspect-square border-2 border-dashed border-black/15 rounded-xl flex flex-col items-center justify-center text-[#4A5568] hover:border-[#E53935]/40 hover:text-[#E53935] cursor-pointer text-xs">
                                                {uploadingIdx === idx ? <Loader2 className="w-6 h-6 animate-spin" /> : <><ImageIcon className="w-6 h-6 mb-1" strokeWidth={1.5} /> Add photo</>}
                                                <input type="file" accept="image/*" data-testid={`submit-image-input-${idx}`} className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], idx)} />
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="bg-[#F4F1EA] rounded-2xl p-4 text-xs text-[#4A5568] space-y-1">
                                <p className="flex items-center gap-1.5 text-[#1C252E] font-bold uppercase tracking-wider"><ShieldCheck className="w-3.5 h-3.5 text-[#E53935]" /> Community standards</p>
                                <p>• <strong>Strictly free</strong> — no money is to change hands through this board.</p>
                                <p>• A moderator will review your listing before it goes public.</p>
                                <p>• When someone orders, you&apos;ll get their name + contact info by email.</p>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-black/5">
                                <p className="text-xs text-[#4A5568]">Posting as <strong>{user.name || user.email}</strong></p>
                                <button type="submit" disabled={submitting} data-testid="submit-form-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-6 py-2.5 text-sm font-medium transition">
                                    {submitting ? "Submitting…" : "Submit for review →"}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default ServiceSubmit;
